import { AlertCircle, Check, Clipboard, Download, Loader2, Package } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { packBundle } from "@/lib/api";
import { copyJson } from "@/lib/clipboard";
import { downloadBlob, downloadJSON } from "@/lib/download";
import type { FlatBundle, Slide } from "@/lib/types";

type Props = {
  bundle: FlatBundle;
  selectedSlide: Slide | undefined;
};

type Toast = { kind: "ok" | "error"; msg: string };

export function ActionsPanel({ bundle, selectedSlide }: Props) {
  const [toast, setToast] = useState<Toast | null>(null);
  const [packBusy, setPackBusy] = useState(false);

  const flash = (msg: string, kind: Toast["kind"] = "ok") => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2200);
  };

  // Strip extension and sanitize for a.download attribute.
  // Keep Japanese / unicode — browsers handle it; just scrub characters unsafe for filesystems.
  const baseName =
    bundle.manifest.source.filename
      .replace(/\.pptx$/i, "")
      .replace(/[\\/:*?"<>|]/g, "_")
      .trim() || "pptx-json-studio";

  const dlFlatJson = () => {
    downloadJSON(bundle, `${baseName}.json`);
    flash("単一 JSON を DL");
  };

  const dlPack = async () => {
    if (packBusy) return;
    setPackBusy(true);
    try {
      const blob = await packBundle(bundle);
      downloadBlob(blob, `${baseName}.zip`);
      flash("分割パック ZIP を DL");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      flash(`ZIP 作成失敗: ${msg}`, "error");
    } finally {
      setPackBusy(false);
    }
  };

  const dlSlide = () => {
    if (!selectedSlide) return;
    const name = `${baseName}-slide-${String(selectedSlide.slide_index).padStart(2, "0")}.json`;
    downloadJSON(selectedSlide, name);
    flash(`slide-${selectedSlide.slide_index} を DL`);
  };

  const copyStructure = async () => {
    if (await copyJson(bundle.structure)) flash("structure をコピー");
    else flash("コピーに失敗", "error");
  };

  const copyRaw = async () => {
    if (await copyJson(bundle)) flash("全体をコピー");
    else flash("コピーに失敗", "error");
  };

  const copySlide = async () => {
    if (!selectedSlide) return;
    if (await copyJson(selectedSlide)) flash(`slide-${selectedSlide.slide_index} をコピー`);
    else flash("コピーに失敗", "error");
  };

  return (
    <div className="flex flex-col gap-6 rounded-[14px] border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <section>
        <h3 className="mb-3 font-display text-sm font-medium uppercase tracking-wide text-[var(--color-ink-muted)]">
          ダウンロード
        </h3>
        <div className="flex flex-col gap-2">
          <Button variant="secondary" size="sm" onClick={dlFlatJson}>
            <Download size={14} /> 単一 JSON
          </Button>
          <Button variant="secondary" size="sm" onClick={dlPack} disabled={packBusy}>
            {packBusy ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
            {packBusy ? "ZIP 生成中…" : "分割パック ZIP"}
          </Button>
          <Button variant="ghost" size="sm" onClick={dlSlide} disabled={!selectedSlide}>
            <Download size={14} /> このスライドだけ
          </Button>
        </div>
        <p className="mt-2 text-[11px] leading-snug text-[var(--color-ink-soft)]">
          単一 JSON は 1 ファイルで持ち運びしやすく、分割パックはスライドごとにシャード化して AI
          に部分だけ渡せる構造になっています。
        </p>
      </section>
      <section>
        <h3 className="mb-3 font-display text-sm font-medium uppercase tracking-wide text-[var(--color-ink-muted)]">
          コピー
        </h3>
        <div className="flex flex-col gap-2">
          <Button variant="secondary" size="sm" onClick={copyStructure}>
            <Clipboard size={14} /> structure のみ
          </Button>
          <Button variant="ghost" size="sm" onClick={copyRaw}>
            <Clipboard size={14} /> 全体（重量注意）
          </Button>
          <Button variant="ghost" size="sm" onClick={copySlide} disabled={!selectedSlide}>
            <Clipboard size={14} /> このスライドだけ
          </Button>
        </div>
        <p className="mt-2 text-[11px] leading-snug text-[var(--color-ink-soft)]">
          コピーした JSON を ChatGPT / Gemini に貼り付けて編集後、復元ページに貼り付けて .pptx
          を再生成できます。
        </p>
      </section>
      {toast && (
        <div
          className={
            toast.kind === "ok"
              ? "flex items-center gap-2 rounded-[10px] bg-[var(--color-coral-soft)]/40 px-3 py-2 text-sm text-[var(--color-coral-dark)]"
              : "flex items-start gap-2 rounded-[10px] border border-[var(--color-coral-dark)] bg-[var(--color-coral-soft)]/40 px-3 py-2 text-sm text-[var(--color-coral-dark)]"
          }
        >
          {toast.kind === "ok" ? <Check size={14} /> : <AlertCircle size={14} />}
          <span className="break-all">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}
